// CancelBookingModal — used by:
//   - BookingDetailPage (header "Cancel booking" action)
//   - DiaryRow (legacy 4-icon Action column "Delete" icon)
//   - CancellationsPage's "Confirm cancellation" action passes
//     `mode="confirm"` so the modal acts as a confirm-only step.
//
// Server contract (server/src/modules/scrap/bookings/bookings.types.ts):
//   POST /api/v1/collections/bookings/cancel  body: { unique_id, reason, destination }
//   destination: 'bin' | 'pending'
//     - 'bin'     — booking moves to status='cancelled' (terminal)
//     - 'pending' — booking returns to the pending queue
// Per Wave B3 brief: the destination acts as the "cancellation type" Select
// (server has no preset reason codes — `reason` is freeform 1–2000 chars).

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { Textarea } from '../../../components/form/Textarea';
import { Select } from '../../../components/form/Select';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { useToast } from '../../../hooks/useToast';
import type { CancelDestination, CancelBookingPayload } from '../../../types/api';
import styles from './CancelBookingModal.module.css';

const REASON_MAX = 2000;

const DESTINATION_OPTIONS = [
  { value: 'bin', label: 'Cancel — remove from system' },
  { value: 'pending', label: 'Send back to Pending queue' },
];

export interface CancelBookingModalProps {
  open: boolean;
  /** The booking's enc_id — sent verbatim under `unique_id`. */
  encId: string | null;
  /** Reference for the modal subtitle. Optional. */
  reference?: string | null;
  /** The customer-supplied reason from the public cancel-token flow.
   *  When passed, pre-populates the textarea so the staff member can
   *  edit / append before confirming. */
  initialReason?: string;
  /** Read-only customer-supplied reason rendered prominently above
   *  the form when staff are confirming a customer-initiated
   *  cancellation request (Task 6A). Distinct from `initialReason`
   *  which is editable in the textarea. */
  customerReason?: string | null;
  onClose: () => void;
  /** Called after a successful cancel — the page typically invalidates
   *  the booking query / list query and closes the modal. */
  onCancelled: (encId: string, payload: CancelBookingPayload) => void;
}

export function CancelBookingModal({
  open,
  encId,
  reference,
  initialReason,
  customerReason,
  onClose,
  onCancelled,
}: CancelBookingModalProps): JSX.Element | null {
  const toast = useToast();
  const [reason, setReason] = useState(initialReason ?? '');
  // C8 — default destination is 'pending' so staff must explicitly opt
  // into permanent removal ('bin'). Matches the signed-off behaviour
  // for the cancellations completion brief.
  const [destination, setDestination] = useState<CancelDestination>('pending');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setReason(initialReason ?? '');
      setDestination('pending');
    }
  }, [open, encId, initialReason]);

  const reasonLength = reason.length;
  const reasonRequired = reasonLength === 0;
  const reasonOver = reasonLength > REASON_MAX;
  const canSubmit = !reasonRequired && !reasonOver && !submitting && encId !== null;

  const subtitle = useMemo(() => {
    if (!reference) return null;
    return `Booking ${reference}`;
  }, [reference]);

  async function handleConfirm() {
    if (!canSubmit || !encId) return;
    setSubmitting(true);
    const payload: CancelBookingPayload = {
      unique_id: encId,
      reason,
      destination,
    };
    try {
      await apiClient.post(endpoints.bookings.cancel, payload);
      toast.success(
        destination === 'bin'
          ? 'Booking cancelled'
          : 'Booking returned to the pending queue',
      );
      onCancelled(encId, payload);
      onClose();
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not cancel booking.'
        : 'Could not cancel booking.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title="Cancel Booking"
      size="md"
      preventClose={submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Keep booking
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            loading={submitting}
            disabled={!canSubmit}
          >
            Cancel booking
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
        {customerReason && customerReason.trim().length > 0 ? (
          <div
            className={styles.customerReasonBox}
            role="note"
            aria-label="Customer's reason for cancellation"
          >
            <span className={styles.customerReasonLabel}>
              Customer&apos;s reason for cancellation:
            </span>
            <span className={styles.customerReasonContent}>
              {customerReason}
            </span>
          </div>
        ) : null}
        <div className={styles.field}>
          <label className={styles.label} htmlFor="cancel-destination">
            Cancellation type
            <span className={styles.required} aria-hidden>*</span>
          </label>
          <Select
            id="cancel-destination"
            options={DESTINATION_OPTIONS}
            value={destination}
            onChange={(e) => setDestination(e.target.value as CancelDestination)}
            aria-label="Cancellation type"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="cancel-reason">
            Reason
            <span className={styles.required} aria-hidden>*</span>
          </label>
          <Textarea
            id="cancel-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            autoResize
            placeholder="Why is this booking being cancelled?"
            aria-required="true"
            aria-invalid={reasonOver || undefined}
          />
          <span
            className={
              reasonOver ? styles.counterError : styles.counter
            }
          >
            {reasonLength} / {REASON_MAX}
          </span>
        </div>
      </div>
    </Modal>
  );
}
